import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * How many of a header bar's items fit across its space, the rest going under
 * a "More" item.
 *
 * A theme's bar is drawn for its own sample shop - "Rings", "Earrings" - and a
 * merchant's shop names its shelves "Nutrition & Supplements". A bar that
 * cannot wrap then spills over the logo and the shopper's corner on both
 * sides. This measures instead of guessing: `measure` is an invisible copy of
 * the bar holding every item in the same type, `space` is the room the real
 * bar has, and the answer is the longest run of items that fits - leaving room
 * for the "More" item whenever any are left over.
 *
 * The copy's last child stands for "More", and it has to be the same kind of
 * element the real one is - a button. A button does not take the bar's
 * `uppercase` (the base styles reset it), so a span standing in for it in an
 * uppercase bar measures "MORE", eight pixels wider than the "More" that is
 * drawn, and the bar gives up an item it had room for.
 *
 * Re-measured when the space resizes and when the web fonts arrive, since a
 * label is a different width in the fallback face. Until the first
 * measurement every item shows, which is what a server render or a test
 * without layout sees.
 *
 * `reserveMore` is for a bar whose "More" is there whatever fits - one capped
 * below what it holds, so there are items left over before any is measured:
 * room for it is kept even when every measured item would fit on its own.
 */
export function useFittingCount(
  total: number,
  { reserveMore = false }: { reserveMore?: boolean } = {},
): {
  space: RefObject<HTMLDivElement | null>;
  measure: RefObject<HTMLDivElement | null>;
  count: number;
} {
  const space = useRef<HTMLDivElement | null>(null);
  const measure = useRef<HTMLDivElement | null>(null);
  const [count, setCount] = useState(total);

  useLayoutEffect(() => {
    const room = space.current;
    const row = measure.current;
    if (!room || !row) return;

    const fit = () => {
      // The box's own fractional width, not `clientWidth`: that is rounded to
      // a whole pixel, and a bar whose labels add up to 288.03px in a room of
      // 288.33px would read as not fitting a room of 288.
      const available = room.getBoundingClientRect().width;
      const children = Array.from(row.children) as HTMLElement[];
      // The last child of the measuring row is the "More" item itself.
      const more = children.at(-1);
      const items = children.slice(0, -1);
      if (available <= 0 || items.length === 0 || !more) {
        setCount(items.length);
        return;
      }
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      const widths = items.map((item) => item.getBoundingClientRect().width);
      const all = widths.reduce((sum, width) => sum + width, 0) + gap * (widths.length - 1);
      if (all <= available && !reserveMore) {
        setCount(widths.length);
        return;
      }
      const moreWidth = more.getBoundingClientRect().width + gap;
      let used = 0;
      let fits = 0;
      for (const width of widths) {
        const next = used + (fits > 0 ? gap : 0) + width;
        if (next + moreWidth > available) break;
        used = next;
        fits += 1;
      }
      setCount(fits);
    };

    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(fit);
    observer.observe(room);
    observer.observe(row);
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) fit();
    });
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [total, reserveMore]);

  return { space, measure, count: Math.min(count, total) };
}
